import React, { useState } from 'react';
import api from '../../services/api';

/**
 * Module d'affichage et de gestion des objectifs pour les chefs de département et employés
 * Permet de visualiser les objectifs assignés aux employés et de cocher ceux qui sont atteints
 */
export default function ObjectifsEmployesModule({ employes, onRefresh, isChef = true, employeId = null }) {
  const [updating, setUpdating] = useState({});
  const [expandedObjectif, setExpandedObjectif] = useState(null);

  // Gestion du cas où employes n'est pas encore chargé
  if (!employes || !Array.isArray(employes)) {
    return (
      <div className="card">
        <div style={{ padding: '24px', textAlign: 'center', color: '#889' }}>
          <div style={{ fontSize: '40px', marginBottom: '16px' }}>⏳</div>
          <div>Chargement des objectifs...</div>
        </div>
      </div>
    );
  }

  // Filtrer les employés selon le contexte (chef voit tous, employé voit les siens)
  const employesAffiches = isChef ? employes : employes.filter(e => e.id === employeId);

  // Récupérer tous les objectifs de tous les employés
  const tousLesObjectifs = employesAffiches.flatMap(emp => 
    (emp.objectifs || []).map(obj => ({
      ...obj,
      employee: emp,
      employeNom: `${emp.prenom} ${emp.nom}`,
      employeAvatar: emp.av,
      employeColor: emp.color
    }))
  );

  // Calculer les statistiques
  const stats = {
    total: tousLesObjectifs.length,
    atteints: tousLesObjectifs.filter(o => o.statut === 'atteint').length,
    enCours: tousLesObjectifs.filter(o => o.statut === 'en_cours').length,
    enRetard: tousLesObjectifs.filter(o => o.statut === 'retard').length,
    progressionMoyenne: tousLesObjectifs.length > 0 
      ? Math.round(tousLesObjectifs.reduce((sum, o) => sum + (o.progression || 0), 0) / tousLesObjectifs.length)
      : 0
  };

  // Fonction pour cocher/décocher un objectif comme atteint
  const toggleObjectifAtteint = async (objectif) => {
    const key = `${objectif.employee.id}-${objectif.id}`;
    if (updating[key]) return;

    try {
      setUpdating({ ...updating, [key]: true });
      
      const nouvelleProgression = objectif.progression >= 100 ? 0 : 100;
      const nouveauStatut = nouvelleProgression >= 100 ? 'atteint' : 'en_cours';

      await api.objectifs.update(objectif.id, {
        progression: nouvelleProgression,
        statut: nouveauStatut
      });

      if (onRefresh) await onRefresh();
    } catch (error) {
      console.error('Erreur lors de la mise à jour de l\'objectif:', error);
      alert('Erreur lors de la mise à jour de l\'objectif');
    } finally {
      setUpdating({ ...updating, [key]: false });
    }
  };

  // Fonction pour mettre à jour la progression manuellement
  const updateProgression = async (objectif, nouvelleProgression) => {
    const key = `${objectif.employee.id}-${objectif.id}`;
    if (updating[key]) return;

    try {
      setUpdating({ ...updating, [key]: true });
      
      const nouveauStatut = nouvelleProgression >= 100 ? 'atteint' : 
                            nouvelleProgression > 0 ? 'en_cours' : 
                            'non_atteint';

      await api.objectifs.update(objectif.id, {
        progression: nouvelleProgression,
        statut: nouveauStatut
      });

      if (onRefresh) await onRefresh();
      setExpandedObjectif(null);
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
      alert('Erreur lors de la mise à jour');
    } finally {
      setUpdating({ ...updating, [key]: false });
    }
  };

  const getProgressionColor = (pct) => {
    if (pct >= 100) return '#66bb6a';
    if (pct >= 75) return '#C9A84C';
    if (pct >= 50) return '#42a5f5';
    return '#ef8080';
  };

  const getStatutBadge = (statut) => {
    const styles = {
      atteint: { bg: '#66bb6a', text: '✅ Atteint' },
      en_cours: { bg: '#C9A84C', text: '🔄 En cours' },
      retard: { bg: '#ef5350', text: '⚠️ En retard' },
      non_atteint: { bg: '#889', text: '❌ Non atteint' }
    };
    const style = styles[statut] || styles.en_cours;
    return (
      <span style={{ 
        padding: '4px 10px', 
        borderRadius: 6, 
        fontSize: 11, 
        fontWeight: 600, 
        background: style.bg,
        color: '#fff'
      }}>
        {style.text}
      </span>
    );
  };

  if (tousLesObjectifs.length === 0) {
    return (
      <div style={{ 
        padding: '48px 32px', 
        textAlign: 'center',
        background: '#0a1929',
        borderRadius: 12,
        border: '1px solid rgba(255,255,255,.06)'
      }}>
        <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.5 }}>🎯</div>
        <div style={{ fontSize: 15, color: '#889', marginBottom: 8 }}>
          {isChef ? 'Aucun objectif assigné aux employés' : 'Aucun objectif assigné'}
        </div>
        <div style={{ fontSize: 13, color: '#667' }}>
          Les objectifs seront affichés ici une fois créés par la direction.
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 24 }}>
      {/* KPI Statistiques */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', 
        gap: 16 
      }}>
        <div style={{ 
          padding: '20px', 
          borderRadius: 12, 
          background: '#0a1929',
          border: '1px solid rgba(255,255,255,.06)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: 10, color: '#889', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '.8px' }}>
            Total objectifs
          </div>
          <div style={{ fontSize: 32, fontWeight: 600, color: '#C9A84C', fontFamily: "'Cormorant Garamond',serif" }}>
            {stats.total}
          </div>
        </div>

        <div style={{ 
          padding: '20px', 
          borderRadius: 12, 
          background: '#0a1929',
          border: '1px solid rgba(102,187,106,.2)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: 10, color: '#889', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '.8px' }}>
            Atteints
          </div>
          <div style={{ fontSize: 32, fontWeight: 600, color: '#66bb6a', fontFamily: "'Cormorant Garamond',serif" }}>
            {stats.atteints}
          </div>
        </div>

        <div style={{ 
          padding: '20px', 
          borderRadius: 12, 
          background: '#0a1929',
          border: '1px solid rgba(255,255,255,.06)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: 10, color: '#889', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '.8px' }}>
            En cours
          </div>
          <div style={{ fontSize: 32, fontWeight: 600, color: '#C9A84C', fontFamily: "'Cormorant Garamond',serif" }}>
            {stats.enCours}
          </div>
        </div>

        <div style={{ 
          padding: '20px', 
          borderRadius: 12, 
          background: '#0a1929',
          border: '1px solid rgba(255,255,255,.06)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: 10, color: '#889', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '.8px' }}>
            Progression moyenne
          </div>
          <div style={{ fontSize: 32, fontWeight: 600, color: getProgressionColor(stats.progressionMoyenne), fontFamily: "'Cormorant Garamond',serif" }}>
            {stats.progressionMoyenne}%
          </div>
        </div>
      </div>

      {/* Liste des objectifs en cartes */}
      <div style={{ display: 'grid', gap: 16 }}>
        {tousLesObjectifs.map(objectif => {
          const key = `${objectif.employee.id}-${objectif.id}`;
          const isUpdating = updating[key];
          const progressionColor = getProgressionColor(objectif.progression || 0);
          const isExpanded = expandedObjectif === key;

          return (
            <div 
              key={key}
              style={{ 
                background: '#0a1929',
                border: '1px solid rgba(255,255,255,.06)',
                borderRadius: 12,
                overflow: 'hidden',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'rgba(201,168,76,.3)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(255,255,255,.06)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              {/* Barre de progression supérieure */}
              <div style={{ 
                height: 4, 
                background: `linear-gradient(90deg, ${progressionColor} ${objectif.progression || 0}%, rgba(255,255,255,.05) ${objectif.progression || 0}%)` 
              }} />

              <div style={{ padding: '20px 24px' }}>
                {/* En-tête avec employé et checkbox */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  {isChef && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div 
                        style={{ 
                          width: 36, 
                          height: 36, 
                          borderRadius: '50%', 
                          background: objectif.employeColor,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 13,
                          fontWeight: 600,
                          color: '#fff'
                        }}
                      >
                        {objectif.employeAvatar}
                      </div>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>
                          {objectif.employeNom}
                        </div>
                        <div style={{ fontSize: 11, color: '#889' }}>
                          {objectif.employee.poste || 'Employé'}
                        </div>
                      </div>
                    </div>
                  )}

                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    {getStatutBadge(objectif.statut)}
                    
                    {/* Checkbox pour cocher comme atteint */}
                    <button
                      onClick={() => toggleObjectifAtteint(objectif)}
                      disabled={isUpdating}
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 8,
                        border: `2px solid ${objectif.progression >= 100 ? '#66bb6a' : 'rgba(255,255,255,.2)'}`,
                        background: objectif.progression >= 100 ? '#66bb6a' : 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: isUpdating ? 'wait' : 'pointer',
                        transition: 'all 0.2s ease',
                        fontSize: 16
                      }}
                      title={objectif.progression >= 100 ? 'Marquer comme non atteint' : 'Marquer comme atteint'}
                    >
                      {objectif.progression >= 100 ? '✓' : ''}
                    </button>
                  </div>
                </div>

                {/* Titre et description */}
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 15, fontWeight: 600, color: '#fff', marginBottom: 6 }}>
                    🎯 {objectif.titre}
                  </div>
                  {objectif.description && (
                    <div style={{ fontSize: 12, color: '#aab', lineHeight: 1.5 }}>
                      {objectif.description}
                    </div>
                  )}
                </div>

                {/* Progression */}
                <div style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 11 }}>
                    <span style={{ color: '#889', fontWeight: 600 }}>Progression</span>
                    <span style={{ color: progressionColor, fontWeight: 700 }}>{objectif.progression || 0}%</span>
                  </div>
                  <div style={{ 
                    height: 8, 
                    background: 'rgba(255,255,255,.06)', 
                    borderRadius: 4, 
                    overflow: 'hidden' 
                  }}>
                    <div style={{ 
                      height: '100%', 
                      width: `${objectif.progression || 0}%`, 
                      background: progressionColor,
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                </div>

                {/* Bouton pour ajuster la progression manuellement */}
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => setExpandedObjectif(isExpanded ? null : key)}
                    style={{
                      padding: '8px 16px',
                      borderRadius: 8,
                      border: '1px solid rgba(201,168,76,.4)',
                      background: isExpanded ? 'rgba(201,168,76,.2)' : 'rgba(201,168,76,.08)',
                      color: '#C9A84C',
                      cursor: 'pointer',
                      fontSize: 12,
                      fontWeight: 600,
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {isExpanded ? '▲ Masquer' : '📊 Ajuster progression'}
                  </button>
                </div>

                {/* Zone d'ajustement de progression */}
                {isExpanded && (
                  <div style={{ 
                    marginTop: 16, 
                    padding: 16, 
                    background: 'rgba(255,255,255,.02)', 
                    borderRadius: 8,
                    border: '1px solid rgba(255,255,255,.06)'
                  }}>
                    <div style={{ fontSize: 12, color: '#889', marginBottom: 12, fontWeight: 600 }}>
                      Ajuster la progression : {objectif.progression || 0}%
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      step="5"
                      value={objectif.progression || 0}
                      onChange={(e) => updateProgression(objectif, Number(e.target.value))}
                      disabled={isUpdating}
                      style={{
                        width: '100%',
                        accentColor: '#C9A84C',
                        cursor: isUpdating ? 'wait' : 'pointer'
                      }}
                    />
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#667', marginTop: 8 }}>
                      <span>0%</span>
                      <span>50%</span>
                      <span>100%</span>
                    </div>
                  </div>
                )}

                {/* Commentaire si présent */}
                {objectif.commentaire && (
                  <div style={{ 
                    marginTop: 12,
                    padding: 12, 
                    background: 'rgba(255,255,255,.02)', 
                    borderRadius: 6,
                    border: '1px solid rgba(255,255,255,.04)'
                  }}>
                    <div style={{ fontSize: 10, color: '#889', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.5px' }}>
                      💬 Commentaire
                    </div>
                    <div style={{ fontSize: 11, color: '#aab', lineHeight: 1.5, fontStyle: 'italic' }}>
                      {objectif.commentaire}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
